// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// HW.12 (COB-332) — native Ledger ownership-signing flow.
//
// The hardware analogue of `RecoverySignFlow` / `Web3AuthSignFlow`: it drives a
// WebHID Ledger connection in THIS origin, BSM-signs the V06 ownership
// challenge with the vault's user multisig key on-device, wraps it into a
// `bsm-bip137-v1` proof, and posts it to `/prove-v2`. Zero user-app dependency
// — the whole point of the native arm (kickoff decision 1): a user must be able
// to prove ownership even if user-app is unavailable or compromised.
//
// The device stack (`hardware-connect.ts`) is dynamically imported so it never
// touches the entry chunk; it is pre-warmed on mount so the WebHID permission
// prompt stays inside the transient user activation of the button click. HW.5
// timing: the connect gesture happens BEFORE the challenge nonce is issued.
//

import { useEffect, useState } from 'preact/hooks';
import {
  bsmProofFromBase64,
  deviceVaultMultisigMessagePath,
  ownershipChallengeMessage,
} from '@cobault/verifier-key-agent';
import { api, type ProveResponse } from '../api.js';

type FlowState =
  | { kind: 'idle' }
  | { kind: 'connecting' }
  | { kind: 'signing' }
  | { kind: 'error'; message: string }
  | { kind: 'signed' };

/** Map a connect-time failure to user-facing copy. `WrongDeviceAppError` (from
 *  `networkForLedgerApp`) already carries an actionable message; a rejected /
 *  absent HID selection surfaces as a DOMException we translate. Shared with the
 *  on-device contract-ack ceremony (`ConfirmContractPanel`). */
export function connectErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.name === 'WrongDeviceAppError') return err.message;
    if (/no device selected|user cancel|no available/i.test(err.message)) {
      return 'No Ledger was selected. Click Connect and choose your device to continue.';
    }
    if (/must be handling a user gesture|SecurityError/i.test(err.message)) {
      return 'The browser blocked the device prompt. Click Connect again.';
    }
    return `Could not connect to the Ledger: ${err.message}`;
  }
  return 'Could not connect to the Ledger.';
}

/** Map an on-device signing failure to user-facing copy. A user declining on
 *  the device screen surfaces as a Ledger 0x6985 error. Shared with the
 *  on-device contract-ack ceremony (`ConfirmContractPanel`). */
export function signErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (/6985|denied|rejected|condition of use/i.test(err.message)) {
      return 'You declined the signature on the device. Approve the message on-screen to prove ownership.';
    }
    return `The device could not sign the challenge: ${err.message}`;
  }
  return 'The device could not sign the challenge.';
}

export function HardwareSignFlow({
  vaultId,
  derivationIndex,
  onSessionEstablished,
  onCancel,
}: {
  vaultId: string;
  derivationIndex: number;
  onSessionEstablished: (session: ProveResponse) => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<FlowState>({ kind: 'idle' });
  const busy = state.kind === 'connecting' || state.kind === 'signing';

  // Pre-warm the device chunk on mount so the click-time dynamic import is
  // cached and the WebHID prompt stays inside transient user activation.
  useEffect(() => {
    void import('../hardware-connect.js').catch(() => undefined);
  }, []);

  async function handleConnectAndSign() {
    if (busy) return;
    setState({ kind: 'connecting' });

    // Connect first (HW.5: gesture before nonce). The WebHID prompt (first-time
    // authorization) must fire inside this click's activation.
    let conn: Awaited<
      ReturnType<typeof import('../hardware-connect.js')['connectLedger']>
    >;
    try {
      const { connectLedger } = await import('../hardware-connect.js');
      conn = await connectLedger();
    } catch (err) {
      setState({ kind: 'error', message: connectErrorMessage(err) });
      return;
    }

    try {
      setState({ kind: 'signing' });

      const challenge = await api.issueChallenge(vaultId);
      if (!challenge) {
        setState({
          kind: 'error',
          message: 'Could not start ownership challenge. Try again in a minute.',
        });
        return;
      }

      let proof;
      try {
        // Build the message + device path inside the try: an out-of-range
        // device counter (e.g. the hardware tile mis-picked for a software
        // vault with a high index) throws in `deviceVaultMultisigMessagePath`,
        // and must surface as an error rather than an unhandled rejection.
        const message = ownershipChallengeMessage(vaultId, challenge.nonce_hex);
        const path = deviceVaultMultisigMessagePath(conn.network, derivationIndex);
        const signatureBase64 = await conn.signMessage(path, message);
        proof = bsmProofFromBase64(signatureBase64);
      } catch (err) {
        setState({ kind: 'error', message: signErrorMessage(err) });
        return;
      }

      const result = await api.proveV2(vaultId, challenge.nonce_hex, proof);
      if (result.kind === 'ok') {
        setState({ kind: 'signed' });
        onSessionEstablished(result.value);
        return;
      }

      let message2: string;
      switch (result.kind) {
        case 'invalid-signature':
          message2 =
            "That device doesn't control this vault. Check you connected the right Ledger and opened the matching app (Bitcoin for mainnet, Bitcoin Test for signet/testnet).";
          break;
        case 'rate-limited':
          message2 =
            'Too many attempts in a short window. Wait a minute and try again.';
          break;
        case 'expired-or-used-nonce':
          message2 =
            'The ownership challenge expired before you approved it. Please try again.';
          break;
        default:
          message2 = result.message ?? 'Unknown error while proving ownership.';
      }
      setState({ kind: 'error', message: message2 });
    } finally {
      await conn.close().catch(() => undefined);
    }
  }

  if (state.kind === 'signed') {
    return (
      <div class="recovery-card recovery-card--success">
        <h3 class="recovery-title">Ownership verified</h3>
        <p>Loading vault details…</p>
      </div>
    );
  }

  return (
    <div class="recovery-card">
      <h3 class="recovery-title">Connect your Ledger</h3>

      <div class="recovery-warning" role="note">
        Your Ledger signs the ownership challenge <strong>on-device</strong> —
        the message appears on the device screen and you approve it there.
        Nothing is stored on this site, and this flow does not depend on the
        Cobault app. Open the <strong>Bitcoin</strong> app for a mainnet vault,
        or <strong>Bitcoin Test</strong> for a signet / testnet vault.
      </div>

      {state.kind === 'connecting' && (
        <div class="recovery-progress">Waiting for the device…</div>
      )}
      {state.kind === 'signing' && (
        <div class="recovery-progress">
          Approve the message on your Ledger to prove ownership…
        </div>
      )}

      {state.kind === 'error' && (
        <div class="recovery-error" role="alert">
          {state.message}
        </div>
      )}

      <div class="recovery-actions">
        <button
          type="button"
          class="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={handleConnectAndSign}
          disabled={busy}
          data-testid="hardware-sign"
        >
          {state.kind === 'connecting'
            ? 'Connecting…'
            : state.kind === 'signing'
              ? 'Approve on device…'
              : 'Connect Ledger'}
        </button>
      </div>
    </div>
  );
}
